import { useEffect, useMemo, useRef } from "react";

const META_ID = /^[0-9]{5,20}$/;
const GOOGLE_ID = /^(?:G|AW|GT)-[A-Za-z0-9]{4,32}$/;
const TIKTOK_ID = /^[A-Za-z0-9]{8,40}$/;

function loadScript(src, id) {
  if (document.getElementById(id)) return;
  const script = document.createElement("script");
  script.id = id;
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}

function ensureMeta(id) {
  if (!META_ID.test(id)) return;
  const fbq = window.fbq || function fbqStub() {
    if (fbqStub.callMethod) fbqStub.callMethod.apply(fbqStub, arguments);
    else fbqStub.queue.push(arguments);
  };
  if (!window.fbq) {
    window.fbq = fbq;
    if (!window._fbq) window._fbq = fbq;
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = "2.0";
    fbq.queue = [];
    loadScript("https://connect.facebook.net/en_US/fbevents.js", "atomicpay-meta-pixel");
  }
  if (window.__atomicMeta !== id) {
    window.fbq("init", id);
    window.fbq("track", "PageView");
    window.__atomicMeta = id;
  }
}

function ensureGoogle(id) {
  if (!GOOGLE_ID.test(id)) return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() {
    window.dataLayer.push(arguments);
  };
  loadScript(
    `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`,
    "atomicpay-google-tag"
  );
  if (window.__atomicGoogle !== id) {
    window.gtag("js", new Date());
    window.gtag("config", id);
    window.__atomicGoogle = id;
  }
}

function ensureTikTok(id) {
  if (!TIKTOK_ID.test(id)) return;
  if (!window.ttq) {
    const ttq = (window.ttq = []);
    window.TiktokAnalyticsObject = "ttq";
    ttq.methods = [
      "page",
      "track",
      "identify",
      "instances",
      "debug",
      "on",
      "off",
      "once",
      "ready",
      "alias",
      "group",
      "enableCookie",
      "disableCookie",
    ];
    ttq.setAndDefer = function setAndDefer(target, method) {
      target[method] = function deferred() {
        target.push([method].concat(Array.prototype.slice.call(arguments, 0)));
      };
    };
    for (const method of ttq.methods) ttq.setAndDefer(ttq, method);
    ttq.load = function load(pixelId) {
      const src = "https://analytics.tiktok.com/i18n/pixel/events.js";
      ttq._i = ttq._i || {};
      ttq._i[pixelId] = [];
      ttq._i[pixelId]._u = src;
      ttq._t = ttq._t || {};
      ttq._t[pixelId] = +new Date();
      ttq._o = ttq._o || {};
      ttq._o[pixelId] = {};
      loadScript(`${src}?sdkid=${encodeURIComponent(pixelId)}&lib=ttq`, "atomicpay-tiktok-pixel");
    };
  }
  if (window.__atomicTikTok !== id) {
    window.ttq.load(id);
    window.ttq.page();
    window.__atomicTikTok = id;
  }
}

function money(product) {
  const variant = product?.variants?.find((item) => item?.amountMinor != null) || product?.variants?.[0];
  if (!variant || variant.amountMinor == null) return {};
  return {
    value: Number(variant.amountMinor) / 100,
    currency: variant.currency || "BRL",
  };
}

export default function StorePixels({ store, product = null, purchase = null }) {
  const skip = useMemo(() => {
    const search = new URLSearchParams(window.location.search);
    return search.get("preview") === "1" || search.get("builder") === "1";
  }, []);
  const viewed = useRef("");
  const purchased = useRef("");

  useEffect(() => {
    if (skip || !store) return;
    ensureMeta(String(store.metaPixelId || ""));
    ensureGoogle(String(store.googleTagId || ""));
    ensureTikTok(String(store.tiktokPixelId || ""));
  }, [skip, store]);

  useEffect(() => {
    if (skip || !product?.id || viewed.current === product.id) return;
    if (!window.fbq && !window.gtag && !window.ttq) return;
    viewed.current = product.id;
    const price = money(product);
    const id = product.handle || product.id;
    if (window.fbq) {
      window.fbq("track", "ViewContent", {
        content_ids: [id],
        content_type: "product",
        content_name: product.title,
        ...price,
      });
    }
    if (window.gtag) {
      window.gtag("event", "view_item", {
        currency: price.currency,
        value: price.value,
        items: [{ item_id: id, item_name: product.title }],
      });
    }
    if (window.ttq) {
      window.ttq.track("ViewContent", {
        contents: [{ content_id: id, content_name: product.title }],
        ...price,
      });
    }
  }, [product, skip, store]);

  useEffect(() => {
    if (skip || !purchase?.id || purchased.current === purchase.id) return;
    if (!window.fbq && !window.gtag && !window.ttq) return;
    purchased.current = purchase.id;
    const value = Number(purchase.value);
    const currency = purchase.currency || "BRL";
    if (window.fbq) {
      window.fbq("track", "Purchase", {
        value,
        currency,
        content_ids: purchase.contentIds || [],
        content_type: "product",
      });
    }
    if (window.gtag) {
      window.gtag("event", "purchase", {
        transaction_id: purchase.id,
        value,
        currency,
      });
    }
    if (window.ttq) {
      window.ttq.track("CompletePayment", {
        value,
        currency,
        content_id: purchase.id,
      });
    }
  }, [purchase, skip, store]);

  return null;
}
